import React, { useEffect, useMemo, useState } from 'react';
import { authStorage } from '../../utils/storage';
import PageHead from '../general/PageHead';
import { Search, RefreshCw, Plus } from 'lucide-react';

const UsersAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getToken();
      const res = await fetch('/api/admin/users', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load users');
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const updateUser = async (id, patch) => {
    const token = authStorage.getToken();
    const res = await fetch(`/api/admin/users/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(patch)
    });
    if (res.ok) fetchUsers();
  };

  const resetPassword = async (id) => {
    const token = authStorage.getToken();
    const res = await fetch(`/api/admin/users/${id}/reset-password`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (res.ok) fetchUsers();
  };

  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [extFilter, setExtFilter] = useState('All');
  const [disabledOnly, setDisabledOnly] = useState(false);

  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== 'All' && (u.Role || 'user') !== roleFilter) return false;
      if (extFilter === 'Internal' && !!u.isExternal) return false;
      if (extFilter === 'External' && !u.isExternal) return false;
      if (disabledOnly && !u.isDisabled) return false;
      if (!text) return true;
      const name = `${u.EmployeeFirst || ''} ${u.EmployeeLast || ''}`.toLowerCase();
      const email = (u.Email || '').toLowerCase();
      const username = (u.Username || '').toLowerCase();
      return name.includes(text) || email.includes(text) || username.includes(text);
    });
  }, [users, q, roleFilter, extFilter, disabledOnly]);

  return (
    <>
      <PageHead
        title="Users"
        sub="Manage accounts, roles, and access"
        actions={
          <>
            <button className="btn btn-secondary" onClick={fetchUsers} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="btn btn-primary" onClick={() => setShowCreate(v => !v)}>
              <Plus size={14} /> Add User
            </button>
          </>
        }
      />

      {showCreate && (
        <div className="card" style={{ marginBottom: 20 }}>
          <CreateUser onCreated={() => { fetchUsers(); setShowCreate(false); }} />
        </div>
      )}

      <div className="toolbar list-toolbar" style={{ flexWrap: 'nowrap', marginBottom: 16 }}>
        <div className="search-box" style={{ width: 240, flexShrink: 0 }}>
          <Search size={14} className="ic" />
          <input
            placeholder="Search name, email, username"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className="dh-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{ height: 36, flexShrink: 0 }}
        >
          <option value="All">All Roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
        <select
          className="dh-select"
          value={extFilter}
          onChange={(e) => setExtFilter(e.target.value)}
          style={{ height: 36, flexShrink: 0 }}
        >
          {['All', 'Internal', 'External'].map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--fg-2)', fontSize: 13.5, cursor: 'pointer', flexShrink: 0 }}>
          <input type="checkbox" checked={disabledOnly} onChange={(e) => setDisabledOnly(e.target.checked)} />
          Disabled only
        </label>
        <span className="tiny muted" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>{filtered.length} of {users.length}</span>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 6, background: 'var(--color-danger-soft)', color: '#8a2d2d', marginBottom: 12, fontSize: 13.5 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Type</th>
                <th style={{ textAlign: 'center' }}>Disabled</th>
                <th style={{ textAlign: 'center' }}>Must Reset</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-3)' }}>Loading users…</td>
                </tr>
              ) : filtered.map(u => (
                <tr key={u.id}>
                  <td className="card-title" style={{ fontWeight: 500 }}>{`${u.EmployeeFirst || ''} ${u.EmployeeLast || ''}`.trim() || u.Username || u.Email}</td>
                  <td data-label="Email" style={{ color: 'var(--fg-2)', fontSize: 13 }}>{u.Email}</td>
                  <td data-label="Role">
                    <select
                      value={u.Role || 'user'}
                      onChange={(e) => updateUser(u.id, { role: e.target.value })}
                      style={{
                        fontSize: 13, color: 'var(--fg-1)', background: 'var(--bg-surface)',
                        border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)',
                        padding: '4px 8px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                      }}
                    >
                      {['admin', 'user'].map(r => (
                        <option key={r} value={r}>{r === 'admin' ? 'Admin' : 'User'}</option>
                      ))}
                    </select>
                  </td>
                  <td data-label="Type">
                    <span style={{
                      display: 'inline-block',
                      fontSize: 11,
                      padding: '3px 8px',
                      borderRadius: 999,
                      background: u.isExternal ? 'var(--color-warning-soft)' : 'var(--bg-accent-soft)',
                      color: u.isExternal ? '#92400e' : 'var(--accent)',
                      border: `1px solid ${u.isExternal ? '#fde68a' : 'var(--accent-border)'}`,
                      fontWeight: 500
                    }}>
                      {u.isExternal ? 'External' : 'Internal'}
                    </span>
                  </td>
                  <td data-label="Disabled" style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!u.isDisabled}
                      onChange={(e) => updateUser(u.id, { isDisabled: e.target.checked })}
                    />
                  </td>
                  <td data-label="Must Reset" style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!u.mustResetPassword}
                      onChange={(e) => updateUser(u.id, { mustResetPassword: e.target.checked })}
                    />
                  </td>
                  <td>
                    {!u.isDisabled && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => resetPassword(u.id)}
                      >
                        Reset Password
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default UsersAdmin;

const CreateUser = ({ onCreated }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('Employee');
  const [accountType, setAccountType] = useState('auto');
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const inferredExternal = useMemo(() => email && !email.toLowerCase().endsWith('@council4.org'), [email]);
  const effectiveExternal = useMemo(() => {
    if (accountType === 'external') return true;
    if (accountType === 'internal') return false;
    return !!inferredExternal;
  }, [accountType, inferredExternal]);

  const doCreate = async () => {
    setError('');
    try {
      setSubmitting(true);
      const token = authStorage.getToken();
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), role, username: username.trim() || undefined, isExternal: effectiveExternal })
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Failed to create user');
      }
      setShowConfirm(false);
      setEmail(''); setFirstName(''); setLastName(''); setRole('Employee'); setUsername('');
      onCreated && onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenConfirm = (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Email is required'); return; }
    setShowConfirm(true);
  };

  return (
    <>
      <form onSubmit={handleOpenConfirm} style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          New User
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
          <div className="dh-field" style={{ flex: '1 1 200px' }}>
            <label className="dh-label">Email</label>
            <input className="dh-input" type="email" placeholder="user@council4.org" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="dh-field" style={{ flex: '1 1 130px' }}>
            <label className="dh-label">First name</label>
            <input className="dh-input" placeholder="First" value={firstName} onChange={e => setFirstName(e.target.value)} />
          </div>
          <div className="dh-field" style={{ flex: '1 1 130px' }}>
            <label className="dh-label">Last name</label>
            <input className="dh-input" placeholder="Last" value={lastName} onChange={e => setLastName(e.target.value)} />
          </div>
          <div className="dh-field" style={{ flex: '1 1 150px' }}>
            <label className="dh-label">Username</label>
            <input className="dh-input" placeholder="optional" value={username} onChange={e => setUsername(e.target.value)} />
          </div>
          <div className="dh-field" style={{ flex: '0 0 auto' }}>
            <label className="dh-label">Role</label>
            <select className="dh-select" value={role} onChange={e => setRole(e.target.value)}>
              {['Admin', 'Manager', 'Staff Rep', 'Membership', 'Employee'].map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="dh-field" style={{ flex: '0 0 auto' }}>
            <label className="dh-label">Account type</label>
            <select className="dh-select" value={accountType} onChange={e => setAccountType(e.target.value)}>
              <option value="auto">Auto (by email)</option>
              <option value="internal">Internal</option>
              <option value="external">External (read-only)</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting || !email}>
            {submitting ? 'Creating…' : 'Create'}
          </button>
        </div>
        {error && <div style={{ color: '#dc2626', marginTop: 8, fontSize: 13 }}>{error}</div>}
      </form>

      {showConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div role="dialog" aria-modal="true" style={{
            background: 'var(--bg-surface)', borderRadius: 'var(--radius)', width: 'min(520px, 92vw)',
            boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-subtle)'
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 600 }}>
              Create User
            </div>
            <div style={{ padding: '16px 20px', display: 'grid', gap: 10 }}>
              <div>
                <div style={{ color: 'var(--fg-3)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Email</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5 }}>{email}</span>
                  <span style={{
                    fontSize: 11, padding: '2px 7px', borderRadius: 999,
                    background: effectiveExternal ? 'var(--color-warning-soft)' : 'var(--bg-accent-soft)',
                    color: effectiveExternal ? '#92400e' : 'var(--accent)',
                    fontWeight: 500
                  }}>{effectiveExternal ? 'External' : 'Internal'}</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12, marginBottom: 2 }}>First name</div>
                  <div style={{ fontSize: 13.5 }}>{firstName || <em style={{ color: 'var(--fg-3)' }}>none</em>}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12, marginBottom: 2 }}>Last name</div>
                  <div style={{ fontSize: 13.5 }}>{lastName || <em style={{ color: 'var(--fg-3)' }}>none</em>}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12, marginBottom: 2 }}>Username</div>
                  <div style={{ fontSize: 13.5 }}>{username || <em style={{ color: 'var(--fg-3)' }}>auto from email</em>}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12, marginBottom: 2 }}>Role</div>
                  <div style={{ fontSize: 13.5 }}>{role}</div>
                </div>
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>
                {effectiveExternal
                  ? 'External users are read-only and cannot access audit logs. They must set a password and verify logins by email.'
                  : 'Internal users can have elevated roles. They must set a password and verify logins by email.'
                }
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={doCreate} disabled={submitting}>
                {submitting ? 'Creating…' : 'Confirm & Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
